import { useState } from 'react';
import { useQueryClient } from 'react-query';
import {
  SlackChannel,
  SlackConfig,
  SlackNotificationEvent,
  SlackSettingsMeta,
  SlackUser,
} from 'api/integrations/slack/slack.types';
import { Option } from 'components/atoms/select';
import { toastService } from 'components/molecules/toast';
import {
  SlackConfigLocalData,
  SlackSettingsLocalData,
} from 'components/organisms/module-settings/integrations/integrations-list/slack/slack.types';
import { useTranslation } from 'hooks/use-translation';
import {
  getUseQuerySlackConfigKey,
  getUseSlackNotificationsSettingsKey,
  useMutateSlackDisconnect,
  useMutateSlackNotificationsSettings,
  useMutateSlackSettings,
  useQuerySlackChannels,
  useQuerySlackNotificationsSettings,
  useQuerySlackUsers,
} from 'queries/slack-integration';

const transformSettingsToApiPayload = (
  data: SlackSettingsLocalData,
): SlackSettingsMeta => {
  let fixedChannel: SlackChannel | undefined;
  let usersToAddToChannel: SlackUser[] | undefined;

  const {
    fixedChannel: _fixedChannel,
    selectedUsers: _selectedUsers = [],
    ...rest
  } = data;

  if (_fixedChannel) {
    fixedChannel = {
      id: _fixedChannel.value as string,
      name: _fixedChannel.label as string,
    };
  }

  if (_selectedUsers.length) {
    usersToAddToChannel = _selectedUsers.map(
      (selectedUser) =>
        ({ id: selectedUser.value, name: selectedUser.label } as SlackUser),
    );
  }

  return { ...rest, fixedChannel, usersToAddToChannel };
};

const transformConfigToLocalData = (
  data: SlackConfig,
): SlackConfigLocalData => {
  const {
    settingsMeta: { fixedChannel, ...rest },
  } = data;
  let _fixedChannel: Option | undefined;

  if (fixedChannel) {
    _fixedChannel = {
      value: fixedChannel.id,
      label: fixedChannel.name,
    };
  }

  return { ...data, settingsMeta: { ...rest, fixedChannel: _fixedChannel } };
};

export const useSlackActions = (config: SlackConfig) => {
  const [slackLocalConfig, setSlackLocalConfig] =
    useState<SlackConfigLocalData>(transformConfigToLocalData(config));

  const queryClient = useQueryClient();
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);

  const successTitle = useTranslation('success');
  const successMessage = useTranslation(
    'settings.integrations.slack.settings.update.success.message',
  );
  const failureTitle = useTranslation('failure');
  const failureMessage = useTranslation(
    'settings.integrations.slack.settings.update.failure.message',
  );

  const { data: slackNotificationsSettings = [] } =
    useQuerySlackNotificationsSettings(config.integrationId);

  const { data: channelsList, isLoading: loadingChannelsList } =
    useQuerySlackChannels(config.integrationId, {
      onSuccess: async (resp) => {
        const {
          settingsMeta: { fixedChannel },
        } = config;

        const _fixedChannel = resp.find(
          (channel) => channel.id === fixedChannel?.id,
        );

        if (_fixedChannel) {
          setSlackLocalConfig((savedConfig) => ({
            ...savedConfig,
            settingsMeta: {
              ...savedConfig.settingsMeta,
              fixedChannel: {
                label: _fixedChannel.name,
                value: _fixedChannel.id,
              },
            },
          }));
        }
      },
    });

  const { data: usersList, isLoading: loadingUsersList } = useQuerySlackUsers(
    config.integrationId,
    {
      onSuccess: (resp) => {
        const {
          settingsMeta: { usersToAddToChannel = [] },
        } = config;

        const userIdList = usersToAddToChannel.map((user) => user.id);

        const selectedUsers: Option[] = resp
          .filter((user) => userIdList.includes(user.id))
          ?.map((user) => ({ label: user.name, value: user.id }));

        if (selectedUsers.length) {
          setSlackLocalConfig((savedConfig) => ({
            ...savedConfig,
            settingsMeta: {
              ...savedConfig.settingsMeta,
              selectedUsers,
            },
          }));
        }
      },
    },
  );

  const { mutate: disconnectMutation, isLoading: isDisconnecting } =
    useMutateSlackDisconnect();

  const handleDisconnect = () => {
    disconnectMutation(config.integrationId, {
      onSuccess: async () => {
        await queryClient.invalidateQueries(getUseQuerySlackConfigKey());
        setShowDisconnectModal(false);
      },
    });
  };

  const { mutate } = useMutateSlackSettings(config.integrationId, {
    onMutate: () => {
      const previousState = queryClient.getQueryData(
        getUseQuerySlackConfigKey(),
      );

      return previousState as SlackConfig[];
    },
    onSuccess: () => {
      toastService({ title: successTitle, message: successMessage }).success();
    },
    onError: async (error, payload, context) => {
      if (context) {
        await setSlackLocalConfig(transformConfigToLocalData(context[0]));
        queryClient.setQueryData(getUseQuerySlackConfigKey(), context);
      }
      toastService({ title: failureTitle, message: failureMessage }).error();
    },
  });

  const handleSettingsChange = (settingsMeta: SlackSettingsLocalData) => {
    setSlackLocalConfig((savedConfig) => ({ ...savedConfig, settingsMeta }));
    mutate(transformSettingsToApiPayload(settingsMeta));
  };

  const { mutate: notificationUpdateMutation } =
    useMutateSlackNotificationsSettings(config.integrationId, {
      onSuccess: async () => {
        await queryClient.invalidateQueries(
          getUseSlackNotificationsSettingsKey(config.integrationId),
        );
      },
    });

  const handleNotificationSettingsChange = async (
    updatedNotificationSettings: SlackNotificationEvent[],
  ) => {
    const updatedNotifications = slackNotificationsSettings.map(
      (slackNotificationSetting) => {
        const updatedNotification = updatedNotificationSettings.find(
          (n) => n.eventType === slackNotificationSetting.eventType,
        );

        if (updatedNotification) {
          return updatedNotification;
        }

        return slackNotificationSetting;
      },
    );

    await queryClient.setQueryData(
      getUseSlackNotificationsSettingsKey(config.integrationId),
      updatedNotifications,
    );

    return notificationUpdateMutation(updatedNotificationSettings);
  };

  return {
    slackLocalConfig,
    handleDisconnect,
    isDisconnecting,
    showDisconnectModal,
    setShowDisconnectModal,
    channelsList,
    loadingChannelsList,
    usersList,
    loadingUsersList,
    handleSettingsChange,
    slackNotificationsSettings,
    handleNotificationSettingsChange,
  };
};
